import { memo, useEffect, useMemo, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import usePageContext from '../hooks/usePageContext';
import { useComponentVisible } from '../hooks';
import { BlockSpinner } from '../helpers/Spiners';
import history from '../helpers/history';
import axios from '../_actions/axiosInstance';
import NotificationPopup from './Notification';
import config from '../_config/config';

const Notification = ({ item, markAsRead }) => {
  const message = useMemo(() => {
    if (item.action_type === 'invoice_uploaded') {
      const boldPart = item.message
        .replace('An invoice has been uploaded for ', '')
        .replace('issue', '')
        .replace('group', '');
      const msg = item.message.replace(
        boldPart,
        `<span class="mention-str" style="font-weight: bold">${boldPart}</span>`
      );
      return msg;
    }

    if (!item.message.includes('You were mentioned in a new comment')) return item.message;
    const splitedMessage = item.message.split('You were mentioned in a new comment: ');
    if (!splitedMessage[1]) return item.message;
    const baseMessage = splitedMessage[1].substring(1).slice(0, -1);
    let str = '';
    // eslint-disable-next-line array-callback-return
    baseMessage.split('@{').map(part => {
      if (!part.includes('"}')) {
        str += `<span>${part}</span>`;
        return;
      }
      const mentionStr = part.split('"}')[0].replaceAll('@{', '');
      const textPart = part
        .split('"}')
        .slice(1)
        .filter(x => x);
      const mentionObj = JSON.parse(`{${mentionStr}"}`);
      str += `<span class="mention-str" style="font-weight: bold"> ${mentionObj.display}</span>`;
      if (textPart.length) {
        str += `<span>${textPart}</span>`;
      }
    });
    return `You were mentioned in a new comment: "${str}"`;
  }, [item]);
  return (
    <li className="media">
      <button
        type="button"
        className={`reset-btn comment-indicator ${item.status}`}
        disabled={item.status === 'read'}
        onClick={() => markAsRead(item.id)}
        data-tip="as"
        data-for="mark-as-read"
      >
        <div className={`crc-indicator ${item.status}`} />
      </button>
      <div className="media-body">
        {ReactHtmlParser(message)}
        {item.wo_id && (
          <div className="extra-info">
            <div className="wo-label">
              Work order ID:
              <Link className="wo-link" to={`/workorders/${item.wo_id}`}>
                {item.wo_name || 'WO'}
              </Link>
            </div>
            <div className="wo-site">{item.site_name}</div>
          </div>
        )}
        <div className="font-size-sm text-muted mt-1 d-flex justify-content-between">
          <button type="button" className="btn p-0 text-muted font-size-sm disabled">
            {moment(item.created_on, 'x').fromNow()}
          </button>
          {!!item?.link && (
            <button
              type="button"
              className="btn btn-link p-0 font-size-sm"
              onClick={() => history.push(item.link.split('.com')[1])}
            >
              View
            </button>
          )}
        </div>
      </div>
    </li>
  );
};
Notification.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    message: PropTypes.string,
    created_on: PropTypes.string,
    status: PropTypes.string,
    action_type: PropTypes.string,
    link: PropTypes.string,
    wo_id: PropTypes.string,
    wo_name: PropTypes.string,
    site_name: PropTypes.string
  }).isRequired,
  markAsRead: PropTypes.func.isRequired
};

const DataBlock = ({ filteredItems, readNotification }) => {
  useEffect(() => {
    if (filteredItems.length) {
      ReactTooltip.rebuild();
    }
  }, [filteredItems]);
  if (!filteredItems.length) return 'No notifications found';
  return (
    <ul className="media-list">
      <ReactTooltip
        id="mark-as-read"
        place="bottom"
        offset={{ right: 50 }}
        className="protean-tooltip"
      >
        Mark as read
      </ReactTooltip>
      {filteredItems.map(item => (
        <Notification item={item} key={item.id} markAsRead={readNotification} />
      ))}
    </ul>
  );
};

DataBlock.propTypes = {
  filteredItems: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  readNotification: PropTypes.func.isRequired
};

const NotificationWindow = ({ title }) => {
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(
    false,
    true,
    'dropdown-toggle'
  );
  const [filter, setFilter] = useState(false);
  const context = usePageContext();
  // eslint-disable-next-line react/destructuring-assignment
  const items = context[title.toLowerCase()];
  const { loading, markAsRead } = context;

  const icon = title === 'Alerts' ? 'icon-bell2' : 'icon-bubbles5';
  const hasUnRead = items.findIndex(x => x.status === 'unread') > -1;
  const filteredItems = filter ? items.filter(x => x.status === 'unread') : items;

  const readNotification = nid => {
    markAsRead(nid, title.toLowerCase());
  };

  const submit = async () => {
    const type = title === 'Comments' ? 'comment' : 'notification';
    try {
      await axios.get(`${config.api.api_url}${config.api.notifications.mark_all}${type}`);
      await context.getItems();
    } catch (e) {
      toast.info(<NotificationPopup text="Server Error" title="Oops.." />);
    }
  };
  return (
    <li ref={ref} className="nav-item dropdown">
      <button type="button" className="btn btn-link dropdown-toggle caret-0">
        <i className={`${icon} protean-notification-icon`} style={{ color: '#fff' }} />
        {hasUnRead && (
          <span
            style={{ top: '-6px', left: '-7px' }}
            className="badge badge-pill badge-mark ml-auto ml-md-0 protean-pill"
          />
        )}
      </button>
      <div
        className={`protean-notification-window dropdown-menu dropdown-menu-right dropdown-content wmin-md-350${
          isComponentVisible ? ' show' : ''
        }`}
      >
        <div className="dropdown-content-header px-0">
          <span className="font-weight-semibold notification-window-title">{title}</span>
          {title !== 'Comments' && (
            <button
              type="button"
              className="protean-btn setting ml-auto"
              onClick={() => {
                history.push('/notifications');
                setIsComponentVisible(false);
              }}
            >
              Notification Settings
            </button>
          )}
        </div>
        <div className="dropdown-content-body dropdown-scrollable">
          {loading ? (
            <BlockSpinner />
          ) : (
            <DataBlock filteredItems={filteredItems} readNotification={readNotification} />
          )}
        </div>
        <div className="dropdown-content-footer">
          <button type="button" className="protean-btn mr-auto" onClick={() => setFilter(!filter)}>
            {filter ? 'View all' : 'View unread'}
          </button>
          <button
            type="button"
            className={`readBtn reset-btn ${!hasUnRead && 'd-none'}`}
            onClick={submit}
          >
            Mark all as read
          </button>
        </div>
      </div>
    </li>
  );
};

NotificationWindow.propTypes = {
  title: PropTypes.string.isRequired
};

export default memo(NotificationWindow);
